import { useDebounceFn } from '@vueuse/core'

import { persistReportGraphsDrawerWidth } from '@app/shared/api/user'
import {
  REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT,
  REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT,
} from '@app/application/report/constants'

// Vue-to-Slim boundary for the resizable report panes: element lookups
// against the server-rendered layout live here. The pane is sized as a
// percentage of `#main-wrapper` via a CSS variable — server-rendered inline
// from the persisted preference, overwritten by `setWidthPct` in-session,
// and falling back to the `two-drawer-layout-new.scss` default when unset.
const CSS_VARIABLE = '--report-graphs-drawer-width'
const WRAPPER_ID = 'main-wrapper'
export const REPORT_GRAPHS_DRAWER_ID = 'secondary-left-drawer'
// Persistence fires once per drag (on drop) and is fire-and-forget, so this only
// coalesces rapid successive drags — it never gates the live, on-pointermove resize.
const PERSIST_DEBOUNCE_MS = 150

function getWrapper(): HTMLElement | null {
  return document.getElementById(WRAPPER_ID)
}

function getDrawer(): HTMLElement | null {
  return document.getElementById(REPORT_GRAPHS_DRAWER_ID)
}

function clampPct(pct: number): number {
  return Math.min(
    REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT,
    Math.max(REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT, Math.round(pct))
  )
}

function applyToDom(pct: number): void {
  getWrapper()?.style.setProperty(CSS_VARIABLE, `${pct}%`)
}

const schedulePersist = useDebounceFn((pct: number) => {
  void persistReportGraphsDrawerWidth(pct)
}, PERSIST_DEBOUNCE_MS)

// Returns the clamped value it applied so callers can track it without
// re-measuring the DOM.
export function setWidthPct(
  pct: number,
  options: { persist: boolean }
): number {
  const clamped = clampPct(pct)
  applyToDom(clamped)
  if (options.persist) void schedulePersist(clamped)
  return clamped
}

// Current pane width as a % of the wrapper, or null while the layout is absent.
export function readWidthPct(): number | null {
  const wrapper = getWrapper()
  const drawer = getDrawer()
  if (!wrapper || !drawer || wrapper.clientWidth === 0) return null
  return Math.round(
    (drawer.getBoundingClientRect().width / wrapper.clientWidth) * 100
  )
}

// Snapshots the drag geometry — the pane's left edge and the wrapper width,
// both stable while a drag lasts — so the returned converter is pure math,
// with no layout reads on pointermove.
export function beginDragMeasurement(): ((clientX: number) => number) | null {
  const wrapper = getWrapper()
  const drawer = getDrawer()
  if (!wrapper || !drawer || wrapper.clientWidth === 0) return null
  const drawerLeft = drawer.getBoundingClientRect().left
  const wrapperWidth = wrapper.clientWidth
  return (clientX) => ((clientX - drawerLeft) / wrapperWidth) * 100
}
